// cesium-imagery.ts - Manages imagery providers and layers

import {
  Cartesian3,
  Cesium3DTileStyle,
  Cesium3DTileset,
  CesiumTerrainProvider,
  ClippingPolygon,
  ClippingPolygonCollection,
  Credit,
  createOsmBuildingsAsync,
  createWorldImageryAsync,
  createWorldTerrainAsync,
  ImageryLayer,
  IonResource,
  IonWorldImageryStyle,
  OpenStreetMapImageryProvider,
  UrlTemplateImageryProvider,
} from "cesium";
import type { ImageryStyle } from "./cesium-types";

// Keep track of tilesets
let osmTileset: any = null;
let googlePhotorealisticTileset: any = null;

// Export function to get OSM tileset
export function getOsmTileset() {
  return osmTileset;
}

// Function to apply clipping to existing OSM buildings
export function applyOsmBuildingsClipping(mapbounds: any) {
  if (osmTileset && mapbounds) {
    console.log("🎯 Applying clipping to existing OSM buildings");
    setOsmBuildingsClipping(osmTileset, mapbounds);
  }
}

// Main function to change the imagery provider
export async function changeImageryProvider(
  viewer: any,
  style: ImageryStyle,
  isMobileDevice: boolean,
  clippingRectangle?: any,
  mapbounds?: any,
) {
  if (!viewer) {
    return;
  }

  try {
    const targetStyle = style;
    // if (isMobileDevice) {
    //   const isIPhone = /iphone/i.test(navigator.userAgent);
    //   if (
    //     isIPhone &&
    //     (targetStyle === "satellite-buildings" ||
    //       targetStyle === "photorealistic")
    //   ) {
    //     targetStyle = "satellite";
    //   }
    // }

    const imageryLayers = viewer.imageryLayers;
    imageryLayers.removeAll();

    if (googlePhotorealisticTileset && targetStyle !== "photorealistic") {
      viewer.scene.primitives.remove(googlePhotorealisticTileset);
      googlePhotorealisticTileset = null;
    }

    const isMapTiler = targetStyle.startsWith("maptiler");
    const isMapbox = targetStyle.startsWith("mapbox");

    if (!isMapTiler && !isMapbox) {
      viewer.terrainProvider = await createWorldTerrainAsync();
    }

    // Set lighting based on style
    viewer.scene.globe.enableLighting = !isMapTiler && !isMapbox;

    let newProvider: any;

    if (isMapTiler) {
      const key = "uj47eDVyT76qCp4tIJS0";
      const styleName = targetStyle.replace("maptiler-", "");

      const styleInfo = {
        streets: { suffix: "-v2", format: "png" },
        basic: { suffix: "-v2", format: "png" },
        bright: { suffix: "-v2", format: "png" },
        pastel: { suffix: "", format: "png" },
        topo: { suffix: "-v2", format: "png", terrain: true },
        toner: { suffix: "-v2", format: "png" },
        hybrid: { suffix: "", format: "jpg", terrain: true },
        winter: { suffix: "-v2", format: "png" },
        positron: { suffix: "", format: "png" },
      }[styleName];

      if (styleInfo) {
        const url = `https://api.maptiler.com/maps/${styleName}${styleInfo.suffix}/{z}/{x}/{y}.${styleInfo.format}?key=${key}`;
        newProvider = new UrlTemplateImageryProvider({
          url: url,
          minimumLevel: 0,
          maximumLevel: 20,
          tileWidth: 512,
          tileHeight: 512,
          credit: new Credit(
            '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> ' +
              '<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
            true,
          ),
        });

        if (styleInfo.terrain) {
          viewer.terrainProvider = await CesiumTerrainProvider.fromUrl(
            `https://api.maptiler.com/tiles/terrain-quantized-mesh-v2/?key=${key}`,
            {
              requestVertexNormals: true,
            },
          );
        } else {
          viewer.terrainProvider = await createWorldTerrainAsync();
        }
      }
    } else if (isMapbox) {
      const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      const username = import.meta.env.VITE_MAPBOX_USERNAME;
      const customStyleId = import.meta.env.VITE_MAPBOX_STYLE_ID;

      if (!accessToken) {
        console.error(
          "Mapbox access token not found. Please set VITE_MAPBOX_ACCESS_TOKEN in your environment variables.",
        );
        newProvider = new OpenStreetMapImageryProvider({});
      } else {
        const styleName = targetStyle.replace("mapbox-", "");

        let url: string;

        if (styleName === "custom" && username && customStyleId) {
          // Use custom style with user's username and style ID
          url = `https://api.mapbox.com/styles/v1/${username}/${customStyleId}/tiles/512/{z}/{x}/{y}@2x?access_token=${accessToken}`;
        } else {
          // Use predefined Mapbox styles
          const styleIds = {
            streets: "streets-v12",
            outdoors: "outdoors-v12",
            light: "light-v11",
            dark: "dark-v11",
            satellite: "satellite-v9",
            "satellite-streets": "satellite-streets-v12",
          }[styleName];

          if (styleIds) {
            url = `https://api.mapbox.com/styles/v1/mapbox/${styleIds}/tiles/512/{z}/{x}/{y}@2x?access_token=${accessToken}`;
          } else {
            console.error(`Unknown Mapbox style: ${styleName}`);
            newProvider = new OpenStreetMapImageryProvider({});
          }
        }

        if (url) {
          newProvider = new UrlTemplateImageryProvider({
            url: url,
            minimumLevel: 0,
            maximumLevel: 22,
            tileWidth: 512,
            tileHeight: 512,
            credit: new Credit(
              '<a href="https://www.mapbox.com/" target="_blank">&copy; Mapbox</a> ' +
                '<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
              true,
            ),
          });
        }
      }
    } else {
      switch (targetStyle) {
        case "openstreetmap":
          newProvider = new OpenStreetMapImageryProvider({});
          break;
        case "satellite":
          newProvider = await createWorldImageryAsync({
            style: IonWorldImageryStyle.AERIAL,
          });
          break;
        case "photorealistic":
          newProvider = await createWorldImageryAsync({
            style: IonWorldImageryStyle.AERIAL,
          });
          if (!googlePhotorealisticTileset) {
            googlePhotorealisticTileset = await Cesium3DTileset.fromUrl(
              await IonResource.fromAssetId(2275207),
            );

            viewer.scene.primitives.add(googlePhotorealisticTileset);
          }
          break;
        default:
          return;
      }
    }

    if (newProvider) {
      const layer = new ImageryLayer(newProvider, {
        rectangle: clippingRectangle,
      });
      imageryLayers.add(layer);
    }

    if (osmTileset) {
      osmTileset.show = style === "satellite-buildings";
    }

    viewer.scene.requestRender();
  } catch (error) {
    const fallbackProvider = new OpenStreetMapImageryProvider({});
    viewer.imageryLayers.addImageryProvider(fallbackProvider);
    if (osmTileset) {
      osmTileset.show = true;
    }
  }
}

export function setOsmBuildingsColor(
  viewer: any,
  color: string,
  opacity: number,
) {
  if (osmTileset) {
    setTimeout(() => {
      console.log("SET COLOR", color, opacity);
      osmTileset.style = new Cesium3DTileStyle({
        color: `color('${color}', ${opacity})`,
      });
      viewer.scene.requestRender();
    }, 3000);
  }
}

export async function toggleOsmBuildings(
  viewer: any,
  enabled: boolean,
  mapData: any,
  mapbounds?: any,
) {
  if (enabled && !osmTileset) {
    try {
      const osmBuildingsTileset = await createOsmBuildingsAsync();
      if (osmBuildingsTileset) {
        setTimeout(() => {
          viewer.scene.primitives.add(osmBuildingsTileset);
          osmTileset = osmBuildingsTileset;
          osmTileset.style = new Cesium3DTileStyle({
            color: `color('${mapData.osmBuildingColor}', ${mapData.osmBuildingOpacity})`,
          });

          // Apply clipping if mapbounds are provided
          if (mapbounds) {
            setOsmBuildingsClipping(osmTileset, mapbounds);
          }

          viewer.scene.requestRender();
        }, 1000);
      }
    } catch (error: any) {
      console.error("Error creating OSM buildings:", error);
    }
  } else if (!enabled && osmTileset) {
    viewer.scene.primitives.remove(osmTileset);
    osmTileset = null;
    viewer.scene.requestRender();
  }
}

// Function to set clipping for OSM buildings based on mapbounds
export function setOsmBuildingsClipping(tileset: any, mapbounds: any) {
  if (!mapbounds || !tileset) {
    console.warn("No mapbounds or tileset provided for OSM buildings clipping");
    return;
  }

  try {
    // Parse mapbounds if it's a string
    const geoData =
      typeof mapbounds === "string" ? JSON.parse(mapbounds) : mapbounds;

    console.log("🔍 Setting OSM buildings clipping with mapbounds:", geoData);

    if (geoData && geoData.features) {
      // Create clipping polygons from mapbounds features
      const clippingPolygons: ClippingPolygon[] = [];

      geoData.features.forEach((feature: any, index: number) => {
        if (feature.geometry && feature.geometry.coordinates) {
          const { type, coordinates } = feature.geometry;
          console.log(`🔍 Processing feature ${index}: ${type}`);

          if (type === "Polygon") {
            const positions = coordinates[0].map((coord: number[]) =>
              Cartesian3.fromDegrees(coord[0], coord[1]),
            );
            console.log(
              `🔍 Creating polygon with ${positions.length} positions`,
            );
            const polygon = new ClippingPolygon({
              positions: positions,
            });
            clippingPolygons.push(polygon);
          } else if (type === "MultiPolygon") {
            coordinates.forEach(
              (polygonCoords: number[][][], polyIndex: number) => {
                const positions = polygonCoords[0].map((coord: number[]) =>
                  Cartesian3.fromDegrees(coord[0], coord[1]),
                );
                console.log(
                  `🔍 Creating multipolygon part ${polyIndex} with ${positions.length} positions`,
                );
                const polygon = new ClippingPolygon({
                  positions: positions,
                });
                clippingPolygons.push(polygon);
              },
            );
          }
        }
      });

      console.log(`🔍 Created ${clippingPolygons.length} clipping polygons`);

      if (clippingPolygons.length > 0) {
        // Create clipping polygon collection
        const clippingPolygonCollection = new ClippingPolygonCollection({
          polygons: clippingPolygons,
          // Set to true to show only content OUTSIDE the clipping polygons (inverse mask)
          inverse: true,
        });

        // Apply clipping to the OSM buildings tileset
        tileset.clippingPolygons = clippingPolygonCollection;

        console.log(
          "✅ Applied clipping to OSM buildings tileset with inverse=true",
        );
        console.log(
          "🔍 Tileset clippingPolygons property:",
          tileset.clippingPolygons,
        );
      } else {
        console.warn("⚠️ No clipping polygons were created");
      }
    } else {
      console.warn("⚠️ No valid geoData.features found in mapbounds");
    }
  } catch (error) {
    console.error("Error setting OSM buildings clipping:", error);
  }
}
